import { Fragment, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiArrowDown, FiCheck, FiMoreHorizontal, FiPaperclip, FiPlay, FiSend, FiVideo, FiZap } from "react-icons/fi";
import Avatar from "./Avatar";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import "./TextChat.css";

const hora = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const mismoDia = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

// "Hoy", "Ayer" o la fecha. Un chat sin separadores de día es un muro de texto.
function etiquetaDia(ts) {
  const d = new Date(ts);
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  if (mismoDia(d, hoy)) return "Hoy";
  if (mismoDia(d, ayer)) return "Ayer";
  const esteAno = d.getFullYear() === hoy.getFullYear();
  return d.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(esteAno ? {} : { year: "numeric" }),
  });
}

/*
 * Respuestas rápidas para las preguntas automáticas.
 *
 * Al tocar una se escribe en el campo y se enfoca, no se envía: así quien
 * quiera contestar de un toque puede, y quien tenga algo más que contar
 * sigue escribiendo. Es lo que hace que una pregunta se conteste.
 */
const RAPIDAS = {
  "post-entreno": ["Muy bien, con ganas de más", "Me costó bastante", "Los pesos se me quedaron cortos", "Me molestó un ejercicio"],
  "sin-entrenar": ["He entrenado pero no lo apunté", "No he tenido tiempo", "Me vendría mejor algo más corto", "Estoy desanimado"],
  ausente: ["Sigo con ganas, retomo ya", "He tenido mucho lío", "Lo quiero dejar por ahora"],
  racha: ["De energía bien", "Estoy cansado", "Súbeme la carga"],
  ficha: ["La completo ahora", "Hay algo que no entiendo"],
  "sin-rutina": ["Lunes, miércoles y viernes", "Martes y jueves", "Fines de semana"],
  bienvenida: ["Me cuesta el momento de entrenar", "Me cuesta la comida", "Las dos cosas"],
};

// Agrupa mensajes seguidos de la misma persona dentro de la misma franja de
// 5 minutos: una burbuja por idea, no una por pulsación de Enter.
const MARGEN_GRUPO = 5 * 60 * 1000;
function agrupar(messages) {
  const bloques = [];
  for (const msg of messages) {
    const ultimo = bloques.at(-1);
    const nuevoDia = !ultimo || !mismoDia(ultimo.msgs.at(-1).timestamp, msg.timestamp);
    const seguido =
      ultimo &&
      !nuevoDia &&
      ultimo.from === msg.from &&
      Boolean(ultimo.automatico) === Boolean(msg.automatico) &&
      new Date(msg.timestamp) - new Date(ultimo.msgs.at(-1).timestamp) < MARGEN_GRUPO;
    if (seguido) ultimo.msgs.push(msg);
    else bloques.push({ from: msg.from, automatico: msg.automatico, dia: nuevoDia, msgs: [msg] });
  }
  return bloques;
}
const auth = async () => ({ Authorization: `Bearer ${(await getSession()).token}` });

// Las fotos del móvil pesan varios MB: se reducen a JPEG de 1600 px antes de subirlas
async function comprimir(file) {
  const bmp = await createImageBitmap(file);
  const escala = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bmp.width * escala);
  canvas.height = Math.round(bmp.height * escala);
  canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return new Promise((ok) => canvas.toBlob(ok, "image/jpeg", 0.82));
}

const MAX_SEGUNDOS = 5;
const MAX_MB_VIDEO = 40;

// Duración del vídeo leída de sus metadatos; NaN si el navegador no puede abrirlo
function duracion(file) {
  return new Promise((ok) => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    const fin = (seg) => {
      URL.revokeObjectURL(url);
      ok(seg);
    };
    v.preload = "metadata";
    v.onloadedmetadata = () => fin(v.duration);
    v.onerror = () => fin(NaN);
    v.src = url;
  });
}

// Los adjuntos piden token, así que se descargan como blob en vez de ponerlos en src.
// `tipo` es "imagen" o "video"; no se descarga nada hasta que `pedir` es true.
function useAdjunto(tipo, id, pedir) {
  const [url, setUrl] = useState(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!pedir) return;
    let vivo = true;
    let creada;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/${id}/${tipo}`, {
          headers: await auth(),
          responseType: "blob",
        });
        creada = URL.createObjectURL(res.data);
        if (vivo) setUrl(creada);
        else URL.revokeObjectURL(creada);
      } catch {
        if (vivo) setFallo(true);
      }
    })();
    return () => {
      vivo = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [tipo, id, pedir]);

  return { url, fallo };
}

function ChatImagen({ id, onCarga }) {
  const { url, fallo } = useAdjunto("imagen", id, true);
  const visor = useRef();

  if (fallo) return <span className="msg-imagen-fallo">Foto no disponible</span>;
  if (!url) return <span className="msg-imagen-cargando" aria-label="Cargando foto" />;
  return (
    <>
      <button type="button" className="msg-imagen" onClick={() => visor.current.showModal()} aria-label="Ver foto">
        <img src={url} alt="" onLoad={onCarga} />
      </button>
      <dialog ref={visor} className="chat-visor" onClick={() => visor.current.close()}>
        <img src={url} alt="Foto del chat" />
      </dialog>
    </>
  );
}

// El vídeo se descarga al pulsar, para no bajar varios MB por cada uno al abrir el chat
function ChatVideo({ id }) {
  const [ver, setVer] = useState(false);
  const { url, fallo } = useAdjunto("video", id, ver);

  if (!ver)
    return (
      <button type="button" className="msg-video-abrir" onClick={() => setVer(true)}>
        <FiPlay aria-hidden="true" /> Ver vídeo
      </button>
    );
  if (fallo) return <span className="msg-imagen-fallo">Vídeo no disponible</span>;
  if (!url) return <span className="msg-imagen-cargando" aria-label="Cargando vídeo" />;
  return <video className="msg-video" src={url} controls autoPlay playsInline />;
}

// Lista de mensajes + formulario, común al chat del cliente y al del entrenador.
// `mio` es el `from` de quien mira; `onCambio` recibe una función sobre la lista de mensajes.
export default function ChatConversacion({
  messages,
  mio,
  enviarUrl,
  extra,
  placeholder,
  boton,
  onCambio,
  otro, // { id, nombre, apellido } de la otra persona, para su foto
  children,
}) {
  const [text, setText] = useState("");
  const [adjunto, setAdjunto] = useState(null);
  const [progreso, setProgreso] = useState(null);
  const [editando, setEditando] = useState(null);
  const [elegido, setElegido] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [arriba, setArriba] = useState(false);
  const chatEndRef = useRef();
  const listaRef = useRef();
  const galeriaRef = useRef();
  const grabarRef = useRef();
  const textoRef = useRef();

  /*
   * Bajar al final sólo si el usuario ya estaba al final. Si está leyendo algo
   * de más arriba, un mensaje nuevo no le debe arrancar de donde está: se le
   * avisa con el botón de bajar y decide él.
   */
  useEffect(() => {
    const caja = listaRef.current;
    if (!caja) return;
    const alFinal = caja.scrollHeight - caja.scrollTop - caja.clientHeight < 160;
    if (alFinal || messages.at(-1)?.from === mio) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, mio]);

  const alScroll = (e) => {
    const c = e.currentTarget;
    setArriba(c.scrollHeight - c.scrollTop - c.clientHeight > 260);
  };

  const bajar = () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" });

  // La foto llega después del texto y empuja lo de abajo: si se estaba al final, seguir al final
  const alCargarFoto = () => {
    const caja = listaRef.current;
    if (caja && caja.scrollHeight - caja.scrollTop - caja.clientHeight < 400) caja.scrollTop = caja.scrollHeight;
  };

  const limpiar = () => {
    setText("");
    setAdjunto(null);
    setEditando(null);
  };

  // Los vídeos se comprueban al elegirlos: así el aviso llega antes de esperar la subida
  const elegirArchivo = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.type.startsWith("video/")) {
      if (file.size > MAX_MB_VIDEO * 1024 * 1024)
        return setError(`El vídeo pesa más de ${MAX_MB_VIDEO} MB. Grábalo en menor calidad.`);
      const seg = await duracion(file);
      if (!Number.isFinite(seg)) return setError("No se pudo leer el vídeo. Prueba con un MP4.");
      // medio segundo de margen: al grabar a mano es fácil pasarse un poco
      if (seg > MAX_SEGUNDOS + 0.5)
        return setError(`El vídeo dura ${Math.round(seg)} s; el máximo son ${MAX_SEGUNDOS} s.`);
    }
    setError("");
    setAdjunto(file);
  };

  const enviar = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (sending || (!t && !adjunto && !editando?.imagenTipo && !editando?.videoTipo)) return;
    setSending(true);
    try {
      const headers = await auth();
      if (editando) {
        const res = await axios.patch(`${API_URL}/chat/${editando._id}`, { text: t }, { headers });
        onCambio((ms) => ms.map((m) => (m._id === res.data._id ? res.data : m)));
      } else {
        let body = { ...extra, text: t };
        if (adjunto) {
          body = new FormData();
          Object.entries({ ...extra, text: t }).forEach(([k, v]) => body.append(k, v));
          if (adjunto.type.startsWith("video/")) {
            body.append("archivo", adjunto);
          } else {
            const blob = await comprimir(adjunto);
            if (!blob) throw new Error("No se pudo leer la foto");
            body.append("archivo", blob, "foto.jpg");
          }
        }
        const res = await axios.post(enviarUrl, body, {
          headers,
          onUploadProgress: (ev) => ev.progress && setProgreso(Math.round(ev.progress * 100)),
        });
        onCambio((ms) => [...ms, res.data]);
      }
      limpiar();
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.message || (editando ? "No se pudo editar el mensaje." : "No se pudo enviar el mensaje.")
      );
    }
    setProgreso(null);
    setSending(false);
  };

  // Toca una respuesta rápida: se escribe, no se envía. El cliente decide si
  // lo manda tal cual o añade algo.
  const usarRapida = (frase) => {
    setText(frase);
    textoRef.current?.focus();
  };

  const editar = (msg) => {
    setAdjunto(null);
    setEditando(msg);
    setText(msg.text);
    setElegido(null);
    textoRef.current?.focus();
  };

  const borrar = async (msg) => {
    setElegido(null);
    if (!window.confirm("¿Borrar este mensaje? Se borrará también para la otra persona.")) return;
    try {
      await axios.delete(`${API_URL}/chat/${msg._id}`, { headers: await auth() });
      onCambio((ms) => ms.filter((m) => m._id !== msg._id));
      if (editando?._id === msg._id) limpiar();
    } catch {
      setError("No se pudo borrar el mensaje.");
    }
  };

  return (
    <>
      <div className="chat-messages" ref={listaRef} onScroll={alScroll}>
        {children}
        {error && <div className="app-estado app-estado-error">{error}</div>}

        {agrupar(messages).map((bloque) => {
          const esMio = bloque.from === mio;
          const ultimoDelBloque = bloque.msgs.at(-1);
          // "Visto" sólo en el último mensaje propio de toda la conversación:
          // repetirlo en cada burbuja es ruido
          const esElUltimoMio = esMio && messages.filter((m) => m.from === mio).at(-1)?._id === ultimoDelBloque._id;

          return (
            <Fragment key={bloque.msgs[0]._id}>
              {bloque.dia && (
                <div className="chat-dia">
                  <span>{etiquetaDia(bloque.msgs[0].timestamp)}</span>
                </div>
              )}

              <div className={`chat-grupo ${esMio ? "chat-grupo-mio" : "chat-grupo-otro"}`}>
                {!esMio && (
                  <div className="chat-grupo-foto">
                    {bloque.automatico ? (
                      <span className="chat-bot" title="Mensaje automático de la plataforma">
                        <FiZap aria-hidden="true" />
                      </span>
                    ) : (
                      <Avatar
                        userId={otro?.id}
                        nombre={otro?.nombre}
                        apellido={otro?.apellido}
                        tamano={34}
                      />
                    )}
                  </div>
                )}

                <div className="chat-grupo-burbujas">
                  {bloque.automatico && (
                    <span className="chat-etiqueta-auto">
                      <FiZap aria-hidden="true" /> Automático
                    </span>
                  )}

                  {bloque.msgs.map((msg) => (
                    <Fragment key={msg._id}>
                      <div className={`chat-burbuja ${esMio ? "chat-burbuja-mia" : "chat-burbuja-otra"}`}>
                        <div className="msg-text">
                          {msg.imagenTipo && <ChatImagen id={msg._id} onCarga={alCargarFoto} />}
                          {msg.videoTipo && <ChatVideo id={msg._id} />}
                          {msg.text}
                        </div>
                        {esMio && (
                          <button
                            type="button"
                            className="msg-opciones"
                            aria-label="Opciones del mensaje"
                            aria-expanded={elegido === msg._id}
                            onClick={() => setElegido(elegido === msg._id ? null : msg._id)}
                          >
                            <FiMoreHorizontal />
                          </button>
                        )}
                      </div>
                      {elegido === msg._id && (
                        <div className="msg-acciones">
                          <button type="button" onClick={() => editar(msg)}>Editar</button>
                          <button type="button" onClick={() => borrar(msg)}>Borrar</button>
                        </div>
                      )}
                    </Fragment>
                  ))}

                  <span className="msg-time">
                    {ultimoDelBloque.editado && "editado · "}
                    {hora(ultimoDelBloque.timestamp)}
                    {esElUltimoMio && (
                      <span className={ultimoDelBloque.leidoEn ? "msg-visto msg-visto-si" : "msg-visto"}>
                        <FiCheck aria-hidden="true" />
                        {ultimoDelBloque.leidoEn ? "Visto" : "Enviado"}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </Fragment>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {arriba && (
        <button type="button" className="chat-bajar" onClick={bajar} aria-label="Ir al último mensaje">
          <FiArrowDown />
        </button>
      )}

      {/* Si lo último es una pregunta automática, se ofrece contestarla de un toque */}
      {(() => {
        const ultimo = messages.at(-1);
        const frases = ultimo?.automatico && ultimo.from !== mio && RAPIDAS[ultimo.tipoSeguimiento];
        if (!frases || text) return null;
        return (
          <div className="chat-rapidas">
            {frases.map((f) => (
              <button type="button" key={f} onClick={() => usarRapida(f)}>
                {f}
              </button>
            ))}
          </div>
        );
      })()}

      {(editando || adjunto) && (
        <div className="chat-aviso">
          <span>
            {editando
              ? "Editando mensaje"
              : `${adjunto.type.startsWith("video/") ? "Vídeo" : "Foto"}: ${adjunto.name}`}
          </span>
          <button type="button" onClick={limpiar}>
            Cancelar
          </button>
        </div>
      )}
      <form className="chat-form" onSubmit={enviar}>
        {!editando && (
          <>
            <input ref={galeriaRef} type="file" accept="image/*,video/*" hidden onChange={elegirArchivo} />
            {/* capture + solo video/*: en Android (Capacitor) y en el móvil abre directamente la cámara de vídeo */}
            <input ref={grabarRef} type="file" accept="video/*" capture="environment" hidden onChange={elegirArchivo} />
            <button
              type="button"
              className="chat-adjuntar"
              aria-label="Adjuntar foto o vídeo"
              title="Adjuntar foto o vídeo"
              onClick={() => galeriaRef.current.click()}
            >
              <FiPaperclip />
            </button>
            <button
              type="button"
              className="chat-adjuntar"
              aria-label={`Grabar vídeo (máx. ${MAX_SEGUNDOS} s)`}
              title={`Grabar vídeo (máx. ${MAX_SEGUNDOS} s)`}
              onClick={() => grabarRef.current.click()}
            >
              <FiVideo />
            </button>
          </>
        )}
        <input
          ref={textoRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={adjunto ? "Añade un comentario (opcional)..." : placeholder}
          maxLength={1000}
        />
        <button type="submit" disabled={sending} aria-label={editando ? "Guardar" : boton}>
          {sending ? (
            progreso !== null ? `${progreso}%` : "..."
          ) : (
            <>
              <FiSend className="chat-enviar-icono" aria-hidden="true" />
              <span className="chat-enviar-texto">{editando ? "Guardar" : boton}</span>
            </>
          )}
        </button>
      </form>
    </>
  );
}
